import moment from 'moment'

// While payment is required only IN_PROGRESS / FUTURE_CANCELLATION are offered and the future date is
// frozen (the client's payment letter names it). The server enforces both.
export const requiresPayment = record =>
    Number(record.is_payment_required) === 1 || record.status === 'AWAITING_PAYMENT'

// "הוסבר ללקוח" (is_cancel_explained): shown for every status; any one of these roles may toggle it (the API enforces
// the same list). Checked only for a stored 1 (the DB may hand back 1 or '1'); missing / null / 0 is unchecked.
export const CANCEL_EXPLAINED_ROLES = ['admin', 'moderator', 'money', 'agents_moderator']
export const canEditCancelExplained = (roles: string[] | null | undefined) =>
    !!roles && CANCEL_EXPLAINED_ROLES.some(role => roles.indexOf(role) !== -1)
export const isCancelExplained = record => Number(record.is_cancel_explained) === 1
export const cancelExplainedPayload = (requestId, checked: boolean) => ({
    requestId,
    is_cancel_explained: checked ? 1 : 0
})

// The store's { search, filters } -> the API's filterArray (list and filtered export). Empty arrays and
// undefined / null / '' are left out; 0 is a real value and is kept.
export const buildCancellationFilterArray = (filters: { search: object; filters: object }) => {
    const filterArray = []
    Object.keys(filters.filters).forEach(key => {
        const value = filters.filters[key]
        if (Array.isArray(value)) {
            if (value.length > 0) filterArray.push({ [key]: value.slice() })
        } else if (value !== undefined && value !== null && value !== '') {
            filterArray.push({ [key]: value })
        }
    })

    if (Object.keys(filters.search).length > 0) {
        filterArray.push({ ...filters.search })
    }

    return filterArray
}

// "הוסבר ללקוח" filter: the dropdown's selection -> the store filter value. 'all' (the default) and a cleared
// dropdown (undefined) mean no restriction (null, left out of filterArray); 1 / 0 pass through, 0 included.
export const EXPLAINED_FILTER_ALL = 'all'
export const explainedFilterValue = selected => (selected === 1 || selected === 0 ? selected : null)
// The status filter that goes with it: כן / לא search every status; הכל goes back to the default OPEN.
export const explainedFilterStatus = selected => (explainedFilterValue(selected) === null ? ['OPEN'] : [])

// Source statuses and roles of the admin AWAITING_PAYMENT transition; the API's startAwaitingPayment accepts the
// same. Any one of the roles is enough (mixed-role accounts included). Payment completion stays admin / money.
export const AWAITING_PAYMENT_SOURCES = ['OPEN', 'HANDLE_REQUIRE', 'FUTURE_CANCELLATION']
export const AWAITING_PAYMENT_ROLES = ['admin', 'moderator', 'money', 'agents_moderator']

export const canStartAwaitingPayment = (record, roles: string[] | null | undefined) =>
    !!roles &&
    AWAITING_PAYMENT_ROLES.some(role => roles.indexOf(role) !== -1) &&
    !requiresPayment(record) &&
    AWAITING_PAYMENT_SOURCES.indexOf(record.status) !== -1 &&
    !record.awaiting_paid_at

// Actual service date (service_provided_date, "תאריך צריכת שירות"): a strict YYYY-MM-DD calendar date, not after
// today. Not the future-cancellation date and not the request's creation date.
export const isValidServiceDate = (value, today = moment()) => {
    const date = moment(value, 'YYYY-MM-DD', true)
    return date.isValid() && !date.isAfter(today, 'day')
}

// A stored service_provided_date as its calendar day 'YYYY-MM-DD', or '' when missing, empty or invalid. Accepts
// what the list sends (YYYY-MM-DD) or a wall-clock 'YYYY-MM-DD HH:mm:ss', and reads the day from the string itself:
// no Date / UTC conversion, so the day never shifts. An ISO instant ('...T21:00:00.000Z', the raw column in update
// responses) is refused, since its UTC day can be the day before.
export const calendarServiceDate = value => {
    const match = typeof value === 'string' && value.trim().match(/^(\d{4}-\d{2}-\d{2})(?: \d{2}:\d{2}(?::\d{2})?)?$/)
    return match && moment(match[1], 'YYYY-MM-DD', true).isValid() ? match[1] : ''
}

// The status column's read-only line: 'DD/MM/YYYY', or '' to omit it.
export const serviceDateLabel = value => {
    const date = calendarServiceDate(value)
    return date ? moment(date, 'YYYY-MM-DD', true).format('DD/MM/YYYY') : ''
}

const storedServiceDate = record => calendarServiceDate(record && record.service_provided_date)

// A FUTURE_CANCELLATION row's scheduled date, in the modal's format. Prefilled as is: an overdue date stays
// invalid in the save gate, so the operator still has to pick a future one.
const scheduledDate = record => {
    if (!record || record.status !== 'FUTURE_CANCELLATION' || !record.future_cancellation_date) return ''
    const date = moment(record.future_cancellation_date)
    return date.isValid() ? date.format('YYYY-MM-DD 00:00:00') : ''
}

// The awaiting-payment modal's state, rebuilt from the row on every opening (and emptied on closing), so no date
// can carry over from another request.
export const awaitingPaymentState = (record = null) => ({
    awaitingRecord: record,
    awaitingDate: scheduledDate(record),
    awaitingDoc: '',
    awaitingServiceDate: storedServiceDate(record),
    awaitingSpin: false
})

// The future date, the actual service date and documentation are always required.
export const canSubmitAwaitingPayment = (state, isFutureDate: (value) => boolean, today = moment()) =>
    isFutureDate(state.awaitingDate) &&
    isValidServiceDate(state.awaitingServiceDate, today) &&
    !!state.awaitingDoc.trim() &&
    !state.awaitingSpin

// No notification fields: AWAITING_PAYMENT sends nothing.
export const awaitingPaymentPayload = state => ({
    requestId: state.awaitingRecord.id,
    requestStatus: 'AWAITING_PAYMENT',
    future_cancellation_date: state.awaitingDate,
    message: state.awaitingDoc.trim(),
    service_provided_date: state.awaitingServiceDate
})
